import { useEffect, useRef, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"

import type { ChatAppRequest, ChatAppResponse, ResponseMessage } from "@/api/models"
import type { EstimatedTime } from "@/utils/time"

import { videoInsightApi } from "@/api/api"
import { GPT4VInput, VectorFieldOptions } from "@/api/models"
import { useRoutes } from "@/contexts/use-routes"
import { useChat } from "@/features/chat/hooks/use-chat"
import { useChatScroll } from "@/features/chat/hooks/use-chat-scroll"
import { useCurrentSessionType } from "@/hooks/use-current-session-type"
import { logger } from "@/lib/logger"
import { queryKeys } from "@/lib/query-keys"
import { ChatService } from "@/services/chat-service"
import { SettingsService } from "@/services/settings-service"
import { defaultSessionState } from "@/stores/slices/chat-slice"
import { useVideoInsightStore } from "@/stores/video-insight-store"
import { getResponseContent, getSessionState } from "@/utils/response-format-utils"

import { useStartNewChat } from "../../chat/hooks/use-start-new-chat"

export function useVideoInsightChat() {
  const { routes } = useRoutes()

  // Determine session type from current route
  const sessionType = useCurrentSessionType()

  const queryClient = useQueryClient()

  const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null)
  const [settingsService] = useState<SettingsService>(SettingsService.getInstance())
  const [chatService] = useState<ChatService>(ChatService.getInstance())

  const [cancelRequest, setCancelRequest] = useState<(() => void) | null>(null)

  const [estimatedTimeLeft, setEstimatedTimeLeft] = useState<EstimatedTime | null>(null)
  const [elapsedTime, setElapsedTime] = useState(0)

  const videoStore = useVideoInsightStore(sessionType)
  const files = videoStore((state) => state.files)
  const currentSessionId = videoStore((state) => state.currentSessionId)
  const chatState = videoStore((state) => {
    const sessionId = state.currentSessionId
    if (!sessionId) return defaultSessionState
    return state.sessionStates[sessionId] ?? defaultSessionState
  })
  const changeChatState = videoStore((state) => state.changeChatState)
  const changeTokenCounts = videoStore((state) => state.changeTokenCounts)
  const readActiveSessionId = () => videoStore.getState().currentSessionId

  const inputTokens =
    chatState.tokenCounts.files > 0 ? chatState.tokenCounts.files : chatState.tokenCounts.text

  useChatScroll({ chatMessageStreamEnd, chatState, sessionId: currentSessionId })

  const { handleStartNewChat, handleSharedLeaveRedirect } = useStartNewChat({
    useChatStore: () => videoStore,
    sessionType,
  })

  const moduleRoute = routes.find((route) => route.id === sessionType)
  if (!moduleRoute) {
    throw new Error(`Module route not found for sessionType: ${sessionType}`)
  }
  const overrides = moduleRoute.overrides

  const {
    handleExampleClicked,
    handleSendQuestion,
    handleRemoveFile,
    handleResetFiles,
    handleAddFiles,
    aborted,
    setAborted,
  } = useChat({
    moduleRoute,
    useChatStore: () => videoStore,
    getCustomChatResponse,
  })

  // update estimated time
  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null

    if (chatState.isLoading && estimatedTimeLeft !== null) {
      intervalId = setInterval(() => {
        setElapsedTime((prevElapsedTime) => prevElapsedTime + 1)
      }, 1000)
    } else {
      setElapsedTime(0)
    }

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [chatState.isLoading, estimatedTimeLeft])

  async function getCustomChatResponse({
    question,
    videoId,
    selectedLanguages,
  }: {
    question: string
    videoId?: string
    selectedLanguages?: string[]
  }) {
    const abortController = new AbortController()
    const TIMEOUT_MS = 300 * 60 * 1000 // 300 minutes in ms

    // Set up automatic timeout
    const timeoutId = setTimeout(() => {
      abortController.abort()
      logger.warn("Request timed out after 300 minutes")
    }, TIMEOUT_MS)

    // Set up manual cancellation
    setCancelRequest(() => () => abortController.abort())

    let lastInteraction: [string, ChatAppResponse] | [string, undefined] | undefined = [
      question,
      undefined,
    ]

    if (aborted) {
      setAborted(undefined)
    }

    changeChatState({
      isLoading: true,
      error: undefined,
      lastQuestion: [question, []],
    })

    // Calculate estimated time. use only files or text tokens if files are not present
    const avgEstimatedTime = (inputTokens / 1000) * 30 // 30s per 1000 tokens, for GPT-4o

    const estimatedTime: EstimatedTime = {
      avg: Math.round(avgEstimatedTime),
      startTime: Date.now(),
    }

    setEstimatedTimeLeft(estimatedTime)

    const sessionIdForRequest = readActiveSessionId()
    try {
      const allAnswers = chatState.streamingAnswer
        ? [...chatState.messages, chatState.streamingAnswer]
        : chatState.messages
      const messages: ResponseMessage[] = allAnswers.flatMap((a: [string, ChatAppResponse]) => [
        { content: a[0], role: "user" },
        { content: getResponseContent(a[1]), role: "assistant" },
      ])

      let session = chatService.getOpenSession(sessionType)

      if (!session) {
        session = await chatService.createSession(sessionType, "")
      }

      const request: ChatAppRequest & {
        video_id?: string
        languages?: string[]
      } = {
        video_id: videoId,
        languages: selectedLanguages,
        messages: [...messages, { content: question, role: "user" }],
        stream: true, // streaming is handled manually
        context: {
          overrides: {
            use_gpt4v: false,
            gpt4v_input: GPT4VInput.Texts,
            use_oid_security_filter: false,
            use_groups_security_filter: false,
            vector_fields: [VectorFieldOptions.Embedding],
            use_custom_gpt: true,
            session_id: session?.id,
            ...overrides,
          },
        },
        // ChatAppProtocol: Client must pass on any session state received from the server
        session_state: chatState.messages.length
          ? getSessionState(chatState.messages[chatState.messages.length - 1][1])
          : null,
      }

      if (files.length === 1) {
        changeChatState({ lastQuestion: [question, []] })
      }

      const finalResponse = await videoInsightApi(
        request,
        files,
        settingsService.token,
        abortController.signal
      )

      if (sessionIdForRequest && readActiveSessionId() !== sessionIdForRequest) {
        return
      }

      lastInteraction = [question, finalResponse]
      changeChatState({ messages: [...chatState.messages, lastInteraction] })

      lastInteraction[1].completed = true

      // Invalidate sessions query to show new/updated sessions
      await queryClient.invalidateQueries({
        queryKey: queryKeys.sessionHistory(sessionType),
      })
    } catch (e) {
      if (sessionIdForRequest && readActiveSessionId() !== sessionIdForRequest) {
        return
      }

      if (e instanceof DOMException && e.name === "AbortError") {
        logger.info("Request aborted")
        setAborted(lastInteraction)
      } else {
        logger.error("Error in getCustomChatResponse", e as Error)
        changeChatState({ error: e as Error })
      }
    } finally {
      clearTimeout(timeoutId)
      if (!sessionIdForRequest || readActiveSessionId() === sessionIdForRequest) {
        changeChatState({ isLoading: false })
      }
      setEstimatedTimeLeft(null)
      setElapsedTime(0)
      setCancelRequest(null)
    }
  }

  const handleCancelRequest = () => {
    if (cancelRequest) {
      cancelRequest()
    }
  }

  return {
    chatState,
    files,
    handleResetFiles,
    handleExampleClicked,
    handleSendQuestion,
    handleAddFiles,
    handleCancelRequest,
    handleRemoveFile,
    aborted,
    chatMessageStreamEnd,
    moduleName: moduleRoute.name,
    handleStartNewChat,
    handleSharedLeaveRedirect,
    totalTokenCount: inputTokens,
    changeTokenCounts,
    estimatedTimeLeft,
    elapsedTime,
    currentSessionId,
  }
}
